/** Sign-in card. The button kicks off the full-page redirect to Guardius's
 *  hosted /auth/authorize page where SIWE actually happens; on return the
 *  AuthProvider exchanges the code. Any callback error (e.g. state mismatch)
 *  is surfaced here via the provider.
 *
 *  Two variants: the default console copy, and an "invited" variant shown when
 *  the boot flow captured a pending invite code (`?invite=...`). A failed
 *  pre-sign-in invite outcome (e.g. a malformed `?invite=`) renders its friendly
 *  message alongside any OAuth callback / local error. */

import { useState } from 'react';
import { useAuth, friendlyInviteError } from '../lib/auth';

export default function Login() {
  const { signIn, error, pendingInvite, inviteOutcome } = useAuth();
  const [localError, setLocalError] = useState<string | null>(null);

  const invited = pendingInvite !== null;
  const inviteFailed = inviteOutcome?.status === 'failed';

  const handleSignIn = async () => {
    setLocalError(null);
    try {
      await signIn();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Sign-in failed.');
    }
  };

  const message = localError ?? error;

  return (
    <div className="login">
      <div className="card">
        <h1>Penseur Trading Console</h1>
        {invited && (
          <p className="note">
            You've been invited to this Penseur Trading console. Sign in to accept your
            invite — read access is granted automatically.
          </p>
        )}
        <button type="button" className="btn btn-signin" onClick={handleSignIn}>
          {invited ? 'Accept invite & sign in' : 'Sign in with Ethereum'}
        </button>
        {message && (
          <p className="error" role="alert">
            {message}
          </p>
        )}
        {inviteFailed && (
          <p className="error" role="alert">
            {friendlyInviteError(inviteOutcome.reason)}
          </p>
        )}
        <p className="note">
          Access is enforced server-side — sign in with an invited or provisioned wallet.
        </p>
      </div>
    </div>
  );
}
